import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Header } from '@/components/layout/Header'
import { useNotifications } from '@/hooks/useNotifications'
import { usePageTitle } from '@/hooks/useDocumentHead'
import { 
  notificationIcons, 
  formatNotificationTime, 
  getNotificationColors,
  hasActions,
} from '@/utils/notifications'
import { Bell, Loader2, AlertCircle, X, Check, CheckCheck, User, Users, History } from 'lucide-react'
import type { Notification, NotificationAction } from '@/services/notificationService'

export function Notifications() {
  usePageTitle('Notifications')
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'all' | 'personal' | 'group'>('all')
  const { 
    notifications,
    personalNotifications,
    groupNotifications,
    unreadCount, 
    isLoading, 
    error,
    markAsRead,
    markAllAsRead,
    dismiss,
    executeAction,
  } = useNotifications()

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read when clicked
    if (!notification.read) {
      await markAsRead(notification.id, notification.scope, notification.groupId)
    }

    // If notification has a primary navigation action, navigate there
    const primaryAction = notification.actions?.find(a => a.primary && a.url)
    if (primaryAction?.url) {
      navigate(primaryAction.url)
    }
  }

  const handleActionClick = async (
    e: React.MouseEvent, 
    notification: Notification, 
    action: NotificationAction
  ) => {
    e.stopPropagation()
    
    const result = await executeAction(notification.id, action.type)
    
    // Handle navigation actions
    if (result?.url) {
      navigate(result.url)
    }
  }

  const handleDismiss = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation()
    await dismiss(notification.id)
  }

  const handleMarkAsRead = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation()
    await markAsRead(notification.id, notification.scope, notification.groupId)
  }

  // Calculate unread counts per category
  const personalUnread = personalNotifications.filter(n => !n.read).length
  const groupUnread = groupNotifications.filter(n => !n.read).length

  const renderNotificationCard = (notification: Notification) => {
    const Icon = notificationIcons[notification.type] || Bell
    const colors = getNotificationColors(notification.type, notification.read)
    // Actions should always show if they exist (not hidden when read)
    const showActions = hasActions(notification)
    
    return (
      <Card 
        key={notification.id} 
        className={`transition-colors cursor-pointer hover:bg-muted/50 group ${
          !notification.read ? 'border-l-4 border-l-primary' : ''
        }`}
        onClick={() => handleNotificationClick(notification)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className={`rounded-full p-3 ${colors.bg}`}>
              <Icon className={`h-5 w-5 ${colors.text}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm ${
                    notification.read ? 'text-muted-foreground' : 'font-semibold'
                  }`}>
                    {notification.title}
                  </p>
                  {notification.scope === 'group' && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">
                      <Users className="h-3 w-3 mr-1" />
                      {notification.groupName || 'Group'}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!notification.read && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => handleMarkAsRead(e, notification)}
                      title="Mark as read"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={(e) => handleDismiss(e, notification)}
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {notification.message}
              </p>
              
              {/* Action buttons - always show if notification has actions */}
              {showActions && (
                <div className="flex gap-2 mt-3">
                  {notification.actions?.map((action, idx) => (
                    <Button
                      key={idx}
                      variant={action.primary ? 'default' : 'outline'}
                      size="sm"
                      onClick={(e) => handleActionClick(e, notification, action)}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-muted-foreground/70 mt-2">
                {formatNotificationTime(notification.createdAt)}
                {notification.fromUserName && (
                  <span> · from {notification.fromUserName}</span>
                )}
              </p>
            </div>
            {!notification.read && (
              <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-2" />
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  const renderEmptyState = (type: string) => (
    <Card className="p-8">
      <div className="text-center text-muted-foreground">
        <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No {type} notifications</p>
        <p className="text-sm mt-1">We'll notify you when something happens</p>
      </div>
    </Card>
  )

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Notifications</h1>
            <p className="text-muted-foreground">
              {unreadCount > 0 
                ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                : 'All caught up!'
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              asChild
              className="gap-2"
            >
              <Link to="/matches">
                <History className="h-4 w-4" />
                My Matches
              </Link>
            </Button>
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => markAllAsRead()}
                className="gap-2"
              >
                <CheckCheck className="h-4 w-4" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Card className="p-8">
            <div className="text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Failed to load notifications</p>
              <p className="text-sm mt-1">Please try again later</p>
            </div>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="all" className="gap-2">
                All
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {unreadCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="personal" className="gap-2">
                <User className="h-4 w-4" />
                Personal
                {personalUnread > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {personalUnread}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="group" className="gap-2">
                <Users className="h-4 w-4" />
                Groups
                {groupUnread > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {groupUnread}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-2">
              {notifications.length === 0 
                ? renderEmptyState('')
                : notifications.map(renderNotificationCard)
              }
            </TabsContent>

            <TabsContent value="personal" className="space-y-2">
              {personalNotifications.length === 0 
                ? renderEmptyState('personal')
                : personalNotifications.map(renderNotificationCard)
              }
            </TabsContent>

            <TabsContent value="group" className="space-y-2">
              {groupNotifications.length === 0 
                ? renderEmptyState('group')
                : groupNotifications.map(renderNotificationCard)
              }
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  )
}
